import { AlertCircle, Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useProjectContext } from "../../context/ProjectContext";
import { useCreateApiKey } from "../../hooks/useApiKeys";
import { uploadIdVerification } from "../../services/auth";
import {
	listMyRouteConfigs,
	listProjectRouteConfigs,
} from "../../services/route-configs";
import type { CreateApiKeyDto, GenerationType } from "../../types/api-keys";
import { IdVerificationModal } from "../auth/IdVerificationModal";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

interface CreateKeyModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const generationTypes: {
	value: GenerationType;
	label: string;
	description: string;
	comingSoon?: boolean;
}[] = [
	{
		value: "text",
		label: "Text Generation",
		description: "Chat completions and text models",
	},
	{
		value: "image",
		label: "Image Generation",
		description: "Image generation and editing (Coming Soon)",
		comingSoon: true,
	},
	{
		value: "video",
		label: "Video Generation",
		description: "Video generation models (Coming Soon)",
		comingSoon: true,
	},
	{
		value: "audio",
		label: "Audio Generation",
		description: "Speech and audio synthesis (Coming Soon)",
		comingSoon: true,
	},
];

export function CreateKeyModal({ open, onOpenChange }: CreateKeyModalProps) {
	const [step, setStep] = useState<"form" | "success">("form");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [selectedTypes, setSelectedTypes] = useState<GenerationType[]>([]);
	const [dailyLimit, setDailyLimit] = useState("10000");
	const [monthlyLimit, setMonthlyLimit] = useState("300000");
	const [createdKey, setCreatedKey] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [idVerificationModalOpen, setIdVerificationModalOpen] = useState(false);

	const { projects, selectedProject, selectProject } = useProjectContext();
	const { user, checkAuth } = useAuth();
	const createKey = useCreateApiKey();

	// Initialize modal state when it opens
	useEffect(() => {
		if (open) {
			// Reset to form when modal opens
			setStep("form");
			setName("");
			setDescription("");
			setSelectedTypes([]);
			setDailyLimit("10000");
			setMonthlyLimit("300000");
			setCreatedKey(null);
			setCopied(false);

			if (selectedProject) {
				setSelectedProjectId(selectedProject.id);
			} else {
				setSelectedProjectId("");
			}
		}
	}, [open, selectedProject]);

	// Sync selectedProjectId with selectedProject when modal is open and in form mode
	useEffect(() => {
		if (open && step === "form" && selectedProject) {
			if (selectedProject.id !== selectedProjectId) {
				setSelectedProjectId(selectedProject.id);
			}
		}
	}, [open, step, selectedProject, selectedProjectId]);

	const handleTypeToggle = (type: GenerationType) => {
		// Don't allow toggling coming soon features
		const generationType = generationTypes.find((t) => t.value === type);
		if (generationType?.comingSoon) return;

		setSelectedTypes((prev) =>
			prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
		);
	};

	const checkProjectHasNsfwModels = async (
		projectId: string,
	): Promise<boolean> => {
		// Always check user's NSFW models as the primary check
		// This is more reliable and catches all cases
		try {
			const allUserConfigs = await listMyRouteConfigs({
				page: 1,
				limit: 1000,
				isActive: true,
			});
			const userHasNsfw = (allUserConfigs.data || []).some(
				(config) => config.isNsfw,
			);

			console.log("[CreateKeyModal] User NSFW check:", {
				userConfigsCount: allUserConfigs.data?.length || 0,
				userHasNsfw,
				nsfwConfigs: allUserConfigs.data
					?.filter((c) => c.isNsfw)
					.map((c) => ({ id: c.id, name: c.name, isNsfw: c.isNsfw })),
			});

			if (userHasNsfw) {
				return true;
			}

			// Also check project-linked models as secondary check
			try {
				const projectConfigs = await listProjectRouteConfigs(projectId, {
					page: 1,
					limit: 1000,
				});
				const projectHasNsfw = (projectConfigs.data || []).some(
					(config) => config.isNsfw,
				);

				console.log("[CreateKeyModal] Project NSFW check (secondary):", {
					projectId,
					projectConfigsCount: projectConfigs.data?.length || 0,
					projectHasNsfw,
					nsfwConfigs: projectConfigs.data
						?.filter((c) => c.isNsfw)
						.map((c) => ({ id: c.id, name: c.name })),
				});

				return projectHasNsfw;
			} catch (projectError) {
				console.warn(
					"[CreateKeyModal] Project model check failed, using user check result:",
					projectError,
				);
				// Return user check result if project check fails
				return userHasNsfw;
			}
		} catch (error) {
			console.error("[CreateKeyModal] Error checking NSFW models:", error);
			// On error, default to requiring verification (safer)
			return true;
		}
	};

	const handleSubmit = async () => {
		if (!selectedProjectId) {
			toast.error("Please select a project");
			return;
		}

		// Update the selected project in context if different
		if (selectedProject?.id !== selectedProjectId) {
			selectProject(selectedProjectId);
		}

		// Check if project has NSFW models and user verification status
		try {
			const hasNsfwModels = await checkProjectHasNsfwModels(selectedProjectId);
			console.log("[CreateKeyModal] NSFW check result:", {
				hasNsfwModels,
				userVerificationStatus: user?.idVerificationStatus,
				userId: user?.id,
			});

			if (hasNsfwModels) {
				const verificationStatus = user?.idVerificationStatus || "pending";
				console.log(
					"[CreateKeyModal] Verification status check:",
					verificationStatus,
				);
				if (verificationStatus !== "approved") {
					console.log("[CreateKeyModal] Showing ID verification modal");
					setIdVerificationModalOpen(true);
					return;
				}
			} else {
				console.log(
					"[CreateKeyModal] No NSFW models found, proceeding with API key creation",
				);
			}
		} catch (error) {
			console.error("[CreateKeyModal] Error checking NSFW models:", error);
			// Continue with creation if check fails
		}

		const data: CreateApiKeyDto = {
			name,
			description: description || undefined,
			allowedGenerationTypes:
				selectedTypes.length > 0 ? selectedTypes : undefined,
			dailyRequestLimit: Number.parseInt(dailyLimit, 10) || 10000,
			monthlyRequestLimit: Number.parseInt(monthlyLimit, 10) || 300000,
		};

		try {
			const result = await createKey.mutateAsync(data);
			// apiFetch unwraps { success: true, data: {...} } to just the data object
			// Backend returns ApiKeyCreatedResponse with key property
			const rawKey =
				(result as any)?.key ?? // Direct key property
				(result as any)?.secretKey ?? // Alternative field name
				(result as any)?.value ?? // Fallback
				"";

			if (!rawKey) {
				console.error("API key not found in response:", result);
				toast.error("API key created but could not retrieve the key value");
				return;
			}

			// Set both states - React will batch these updates
			setCreatedKey(rawKey);
			setStep("success");
			toast.success("API key created", {
				description: "Copy it now — you will not be able to view it again.",
			});
		} catch (error) {
			// Error handling is managed by React Query
			console.error("Failed to create API key:", error);
			toast.error("Failed to create API key");
		}
	};

	const handleCopy = async (e?: React.MouseEvent) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
			if (
				e.nativeEvent &&
				typeof e.nativeEvent.stopImmediatePropagation === "function"
			) {
				e.nativeEvent.stopImmediatePropagation();
			}
		}

		// Copy the created API key
		const keyToCopy = createdKey;

		if (!keyToCopy) {
			toast.error("No API key to copy");
			return;
		}

		try {
			// Try modern clipboard API first
			if (navigator.clipboard?.writeText) {
				try {
					await navigator.clipboard.writeText(keyToCopy);
					setCopied(true);
					setTimeout(() => setCopied(false), 2000);
					toast.success("Copied API key to clipboard");
					return;
				} catch (clipboardError) {
					// If clipboard API fails (e.g., permission denied), fall through to fallback
					console.warn(
						"Clipboard API failed, trying fallback:",
						clipboardError,
					);
				}
			}

			// Fallback for older browsers or when clipboard API fails
			const textArea = document.createElement("textarea");
			textArea.value = keyToCopy;
			textArea.style.position = "fixed";
			textArea.style.left = "-999999px";
			textArea.style.top = "-999999px";
			textArea.style.opacity = "0";
			textArea.setAttribute("readonly", "");
			document.body.appendChild(textArea);

			// Select and copy
			textArea.focus();
			textArea.select();
			textArea.setSelectionRange(0, keyToCopy.length);

			try {
				const successful = document.execCommand("copy");
				if (successful) {
					setCopied(true);
					setTimeout(() => setCopied(false), 2000);
					toast.success("Copied API key to clipboard");
				} else {
					throw new Error("Copy command failed");
				}
			} finally {
				document.body.removeChild(textArea);
			}
		} catch (error) {
			console.error("Failed to copy API key:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			if (
				errorMessage.includes("permission") ||
				errorMessage.includes("denied")
			) {
				toast.error("Clipboard permission denied. Please copy manually.");
			} else {
				toast.error("Failed to copy. Please select and copy manually.");
			}
		}
	};

	const codeElRef = useRef<HTMLElement | null>(null);
	const codeRef = (node: HTMLElement | null) => {
		codeElRef.current = node;
		// Remove click handler - we have dedicated buttons now
	};

	// Calculate displayText synchronously during render
	// Always show full key (no masking) so users can copy it
	const displayText = createdKey || "";

	const handleClose = () => {
		// Reset form
		setStep("form");
		setName("");
		setDescription("");
		setSelectedTypes([]);
		setDailyLimit("10000");
		setMonthlyLimit("300000");
		setCreatedKey(null);
		setCopied(false);
		setSelectedProjectId("");
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-lg" data-nova-dialog="true">
				{step === "form" ? (
					<>
						<DialogHeader>
							<DialogTitle>Create New API Key</DialogTitle>
							<DialogDescription>
								Create a new API key with custom permissions and limits
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-6 py-4">
							{/* Project Selection */}
							<div className="space-y-2">
								<Label htmlFor="project" className="text-sm font-medium">
									Project <span className="text-destructive">*</span>
								</Label>
								<Select
									value={selectedProjectId || undefined}
									onValueChange={(value) => {
										setSelectedProjectId(value);
									}}
									disabled={projects.length === 0}
								>
									<SelectTrigger id="project" className="w-full">
										<SelectValue
											placeholder={
												projects.length === 0
													? "No projects available"
													: "Select a project"
											}
										/>
									</SelectTrigger>
									<SelectContent className="z-[100]">
										{projects.map((project) => (
											<SelectItem key={project.id} value={project.id}>
												<div className="flex flex-col">
													<span className="font-medium">{project.name}</span>
													{project.description && (
														<span className="text-xs text-muted-foreground truncate">
															{project.description}
														</span>
													)}
												</div>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{projects.length === 0 && (
									<p className="text-xs text-muted-foreground">
										Create a project first to generate API keys
									</p>
								)}
							</div>

							{/* Name */}
							<div className="space-y-2">
								<label htmlFor="name" className="text-sm font-medium">
									Key Name <span className="text-destructive">*</span>
								</label>
								<Input
									id="name"
									placeholder="e.g., Production API Key"
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</div>

							{/* Description */}
							<div className="space-y-2">
								<label htmlFor="description" className="text-sm font-medium">
									Description
								</label>
								<Input
									id="description"
									placeholder="Optional description for this key"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
								/>
							</div>

							{/* Permissions */}
							<div className="space-y-3">
								<span className="text-sm font-medium">Permissions</span>
								<p className="text-xs text-muted-foreground">
									Select which generation types this key can access. Leave empty
									for all.
								</p>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
									{generationTypes.map((type) => (
										<div
											key={type.value}
											role={type.comingSoon ? "presentation" : "button"}
											tabIndex={type.comingSoon ? -1 : 0}
											className={`flex items-start space-x-3 p-4 rounded-lg border w-full text-left min-h-[60px] ${
												type.comingSoon
													? "border-border/50 bg-muted/30 opacity-60 cursor-not-allowed"
													: `cursor-pointer transition-colors ${
															selectedTypes.includes(type.value)
																? "border-primary bg-primary/5"
																: "border-border hover:border-primary/50"
														}`
											}`}
											aria-pressed={selectedTypes.includes(type.value)}
											onClick={
												type.comingSoon
													? undefined
													: () => handleTypeToggle(type.value)
											}
											onKeyDown={
												type.comingSoon
													? undefined
													: (e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault();
																handleTypeToggle(type.value);
															}
														}
											}
										>
											<Checkbox
												checked={selectedTypes.includes(type.value)}
												onCheckedChange={
													type.comingSoon
														? undefined
														: () => handleTypeToggle(type.value)
												}
												disabled={type.comingSoon}
											/>
											<div className="space-y-1">
												<div className="flex items-center gap-2">
													<p className="text-sm font-medium leading-none">
														{type.label}
													</p>
													{type.comingSoon && (
														<span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
															Coming Soon
														</span>
													)}
												</div>
												<p
													className={`text-xs ${type.comingSoon ? "text-muted-foreground/70" : "text-muted-foreground"}`}
												>
													{type.description}
												</p>
											</div>
										</div>
									))}
								</div>
							</div>

							{/* Rate Limits */}
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div className="space-y-2">
									<label htmlFor="dailyLimit" className="text-sm font-medium">
										Daily Limit
									</label>
									<Input
										id="dailyLimit"
										type="number"
										placeholder="10000"
										value={dailyLimit}
										onChange={(e) => setDailyLimit(e.target.value)}
									/>
									<p className="text-xs text-muted-foreground">
										Requests per day
									</p>
								</div>
								<div className="space-y-2">
									<label htmlFor="monthlyLimit" className="text-sm font-medium">
										Monthly Limit
									</label>
									<Input
										id="monthlyLimit"
										type="number"
										placeholder="300000"
										value={monthlyLimit}
										onChange={(e) => setMonthlyLimit(e.target.value)}
									/>
									<p className="text-xs text-muted-foreground">
										Requests per month
									</p>
								</div>
							</div>
						</div>

						<DialogFooter>
							<Button variant="outline" onClick={handleClose}>
								Cancel
							</Button>
							<Button
								onClick={handleSubmit}
								disabled={
									!name ||
									!selectedProjectId ||
									projects.length === 0 ||
									createKey.isPending
								}
							>
								{createKey.isPending ? "Creating..." : "Create Key"}
							</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2 text-green-600">
								<Check className="w-5 h-5" />
								API Key Created
							</DialogTitle>
							<DialogDescription>
								Your API key has been created successfully
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-4">
							{step === "success" && (
								<div className="flex items-center gap-2 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
									<AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
									<p className="text-sm text-amber-400">
										Make sure to copy your API key now. You won't be able to see
										it again!
									</p>
								</div>
							)}
							{step === "success" && (
								<div className="space-y-2">
									<label
										className="text-sm font-medium"
										htmlFor="created-api-key"
									>
										Your API Key
									</label>
									<div className="flex items-center gap-2">
										<output
											id="created-api-key"
											ref={codeRef as any}
											aria-live="polite"
											className="flex-1 min-w-0 p-3 bg-muted rounded-lg text-sm font-mono break-all"
										>
											<code className="select-all" data-step={step}>
												{displayText}
											</code>
										</output>
										<div className="flex items-center gap-2 flex-shrink-0">
											<Button
												type="button"
												variant="outline"
												size="icon"
												className={`h-10 w-10 ${copied ? "text-green-600" : ""}`}
												onClick={async (e) => {
													try {
														e.preventDefault();
														e.stopPropagation();
														if (
															e.nativeEvent &&
															typeof e.nativeEvent.stopImmediatePropagation ===
																"function"
														) {
															e.nativeEvent.stopImmediatePropagation();
														}
														await handleCopy(e);
													} catch (error) {
														console.error(
															"[ERROR] Copy button onClick error:",
															error,
														);
													}
												}}
												onMouseDown={(e) => {
													e.preventDefault();
													e.stopPropagation();
													if (
														e.nativeEvent &&
														typeof e.nativeEvent.stopImmediatePropagation ===
															"function"
													) {
														e.nativeEvent.stopImmediatePropagation();
													}
												}}
												aria-label={
													step === "success" ? "Copy API key" : "Copy API key"
												}
											>
												{copied ? (
													<Check className="w-5 h-5" />
												) : (
													<Copy className="w-5 h-5" />
												)}
											</Button>
										</div>
									</div>
								</div>
							)}
						</div>

						<DialogFooter>
							<Button onClick={handleClose}>Done</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
			<IdVerificationModal
				open={idVerificationModalOpen}
				onOpenChange={setIdVerificationModalOpen}
				onUpload={async (file) => {
					await uploadIdVerification(file);
					await checkAuth();
					toast.success(
						"ID verification submitted. Your ID will be reviewed within 24-48 hours.",
					);
					setIdVerificationModalOpen(false);
				}}
				verificationStatus={
					user?.idVerificationStatus as
						| "pending"
						| "approved"
						| "rejected"
						| undefined
				}
			/>
		</Dialog>
	);
}
